import { supabase } from './supabase';

export interface TimedMessage {
  id: string;
  text: string;
  interval: string;
  spacing: string;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
  // Legacy fields for backward compatibility
  message?: string;
  schedule_type?: 'interval' | 'specific_time';
  interval_minutes?: number;
  specific_time?: string;
  specific_timezone?: string;
  specific_days?: number[];
  last_sent_at?: string;
  next_scheduled_at?: string;
  total_sent_count?: number;
}

export interface CreateTimedMessageInput {
  text?: string;
  interval?: string;
  spacing?: string;
  enabled?: boolean;
  // Legacy fields for backward compatibility
  message?: string;
  schedule_type?: 'interval' | 'specific_time';
  interval_minutes?: number;
  specific_time?: string;
  specific_timezone?: string;
  specific_days?: number[];
}

// Rumble chat message limits
export const RUMBLE_MESSAGE_LIMITS = {
  MAX_LENGTH: 500,
  MIN_INTERVAL_MINUTES: 1,
  MAX_INTERVAL_MINUTES: 1440, // 24 hours
};

/**
 * Validate message content and scheduling
 */
export function validateTimedMessage(input: CreateTimedMessageInput, mentionWord: string = 'dork'): string[] {
  const errors: string[] = [];

  // Support both new and legacy field names
  const messageText = input.text || input.message;

  // Message validation
  if (!messageText?.trim()) {
    errors.push('Message is required');
  } else if (messageText.length > RUMBLE_MESSAGE_LIMITS.MAX_LENGTH) {
    errors.push(`Message must be ${RUMBLE_MESSAGE_LIMITS.MAX_LENGTH} characters or less`);
  } else if (messageText.toLowerCase().includes(mentionWord.toLowerCase())) {
    errors.push(`"${mentionWord}" is a reserved keyword and cannot be used in timed messages`);
  }

  // Interval validation - handle both new and legacy formats
  if (input.schedule_type === 'interval') {
    // Legacy format - validate interval_minutes
    if (!input.interval_minutes || input.interval_minutes < 1) {
      errors.push('Interval must be at least 1 minute');
    }
  } else if (!input.schedule_type) {
    // New format - validate interval and spacing
    if (!input.interval?.trim()) {
      errors.push('Interval is required');
    }

    if (!input.spacing?.trim()) {
      errors.push('Spacing is required');
    }
  }

  return errors;
}

/**
 * Get all timed messages for the current user
 */
export async function getTimedMessages(): Promise<TimedMessage[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to view timed messages');
  }
  
  const { data, error } = await supabase.rpc('get_my_timed_messages');
  
  if (error) {
    console.error('Error fetching timed messages:', error);
    throw new Error('Failed to load timed messages');
  }
  
  // Convert JSONB array to TimedMessage array
  return Array.isArray(data) ? data : [];
}

/**
 * Create a new timed message
 */
export async function createTimedMessage(input: CreateTimedMessageInput): Promise<TimedMessage> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to create timed messages');
  }

  // Fetch mention_word from user_rumble_settings
  const { data: rumbleSettings } = await supabase
    .from('user_rumble_settings')
    .select('mention_word')
    .eq('user_id', user.id)
    .single();

  const mentionWord = rumbleSettings?.mention_word || 'dork';

  // Validate input with mention_word
  const errors = validateTimedMessage(input, mentionWord);
  if (errors.length > 0) {
    throw new Error(errors.join(', '));
  }

  const messageText = (input.text || input.message || '').trim();

  // Convert legacy fields to new format
  let messageInterval = (input.interval || '').trim();
  let messageSpacing = (input.spacing || '').trim();

  // If using legacy schedule_type, convert interval_minutes to string
  if (input.schedule_type === 'interval' && input.interval_minutes && !messageInterval) {
    messageInterval = input.interval_minutes.toString();
  }

  // Default spacing if not provided
  if (!messageSpacing) {
    messageSpacing = '1';
  }

  const { data, error } = await supabase.rpc('add_timed_message', {
    message_text: messageText,
    message_interval: messageInterval,
    message_spacing: messageSpacing
  });

  if (error) {
    console.error('Error creating timed message:', error);
    // Check for specific error types
    if (error.message?.includes('NO_RUMBLE_API')) {
      const customError = new Error('NO_RUMBLE_API');
      (customError as any).isRumbleApiError = true;
      throw customError;
    }
    if (error.message?.includes('NOT_WHITELISTED')) {
      const customError = new Error('NOT_WHITELISTED');
      (customError as any).isWhitelistError = true;
      throw customError;
    }
    throw new Error(error.message || 'Failed to create timed message');
  }

  return data;
}

/**
 * Update a timed message
 */
export async function updateTimedMessage(
  id: string,
  updates: Partial<CreateTimedMessageInput & { enabled: boolean }>
): Promise<TimedMessage> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to update timed messages');
  }

  // If updating text, interval, or spacing, validate
  if (updates.text || updates.message || updates.interval || updates.spacing || updates.interval_minutes) {
    // Fetch mention_word from user_rumble_settings
    const { data: rumbleSettings } = await supabase
      .from('user_rumble_settings')
      .select('mention_word')
      .eq('user_id', user.id)
      .single();

    const mentionWord = rumbleSettings?.mention_word || 'dork';

    const errors = validateTimedMessage(updates, mentionWord);
    if (errors.length > 0) {
      throw new Error(errors.join(', '));
    }
  }

  // Convert legacy fields to new format
  const messageText = updates.text || updates.message;
  let messageInterval = updates.interval;
  let messageSpacing = updates.spacing;

  // If using legacy schedule_type, convert interval_minutes to string
  if (updates.schedule_type === 'interval' && updates.interval_minutes && !messageInterval) {
    messageInterval = updates.interval_minutes.toString();
  }

  // Default spacing if not provided
  if (!messageSpacing && (updates.interval || updates.interval_minutes)) {
    messageSpacing = '1';
  }

  const { data, error } = await supabase.rpc('update_timed_message', {
    message_id: id,
    message_text: messageText?.trim(),
    message_interval: messageInterval?.trim(),
    message_spacing: messageSpacing?.trim(),
    message_enabled: updates.enabled
  });

  if (error) {
    console.error('Error updating timed message:', error);
    throw new Error(error.message || 'Failed to update timed message');
  }

  return data;
}

/**
 * Delete a timed message
 */
export async function deleteTimedMessage(id: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('You must be signed in to delete timed messages');
  }
  
  const { error } = await supabase.rpc('delete_timed_message', {
    message_id: id
  });
  
  if (error) {
    console.error('Error deleting timed message:', error);
    throw new Error(error.message || 'Failed to delete timed message');
  }
}

/**
 * Toggle enabled status of a timed message
 */
export async function toggleTimedMessage(id: string, enabled: boolean): Promise<TimedMessage> {
  return updateTimedMessage(id, { enabled });
}

/**
 * Get common interval options for the UI
 */
export function getIntervalOptions() {
  return [
    { value: '5', label: '5 minutes' },
    { value: '10', label: '10 minutes' },
    { value: '15', label: '15 minutes' },
    { value: '30', label: '30 minutes' },
    { value: '60', label: '1 hour' },
    { value: '120', label: '2 hours' },
    { value: '180', label: '3 hours' },
    { value: '360', label: '6 hours' },
    { value: '720', label: '12 hours' },
    { value: '1440', label: '24 hours' },
  ];
}

/**
 * Get common spacing options for the UI  
 */
export function getSpacingOptions() {
  return [
    { value: '1', label: '1 message apart' },
    { value: '2', label: '2 messages apart' },
    { value: '3', label: '3 messages apart' },
    { value: '5', label: '5 messages apart' },
    { value: '10', label: '10 messages apart' },
    { value: '15', label: '15 messages apart' },
    { value: '20', label: '20 messages apart' },
    { value: '25', label: '25 messages apart' },
  ];
}

/**
 * Get common timezone options (legacy compatibility)
 */
export function getTimezoneOptions() {
  return [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'America/New_York', label: 'Eastern Time (US & Canada)' },
    { value: 'America/Chicago', label: 'Central Time (US & Canada)' },
    { value: 'America/Denver', label: 'Mountain Time (US & Canada)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)' },
    { value: 'Europe/London', label: 'London' },
    { value: 'Europe/Paris', label: 'Paris' },
    { value: 'Europe/Berlin', label: 'Berlin' },
    { value: 'Asia/Tokyo', label: 'Tokyo' },
    { value: 'Asia/Shanghai', label: 'Shanghai' },
    { value: 'Australia/Sydney', label: 'Sydney' },
  ];
}

/**
 * Get day options for scheduling (legacy compatibility)
 */
export function getDayOptions() {
  return [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 7, label: 'Sunday' },
  ];
}